import { useMarketingViewSection } from '../components/MarketingViewsProvider';
import { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import {
  siteDataApi,
  type ChannelTrafficRow,
} from '../api/siteData';
import { ReportExportButton } from '../components/ReportExportButton';
import { TableLoadingCell, TableLoadingOverlay } from '../components/TableLoadingOverlay';
import { useSiteShopName } from '../hooks/useSiteShopName';
import { useAppStore } from '../store/useAppStore';
import { formatSiteMetric, isSiteSensitiveMetric } from '../lib/siteDataSecurity';
import { SortableHeader, useTableSort, type SortValue } from '../components/SortableTable';

// 列格式化
const fmtInt = (v: number) =>
  v == null ? '-' : Math.round(v).toLocaleString('en-US');
const fmtNum = (v: number) =>
  v == null ? '-' : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (v: number) => (v == null ? '-' : (v * 100).toFixed(2) + '%');

// 默认日期：昨天 (站内数据为 T-1)。用本地时区格式化，避免 toISOString 的 UTC 偏移。
function defaultDate(daysAgo = 1): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

type DatePreset = 'today' | 'yesterday' | 'custom';

const DATE_PRESETS: { key: DatePreset; label: string }[] = [
  { key: 'yesterday', label: '昨天' },
  { key: 'custom', label: '自定义' },
];

interface LevelTableProps {
  title: string;
  level: 1 | 2 | 3;
  rows: ChannelTrafficRow[];
  loading: boolean;
  userLevel: number;
  extra?: ReactNode;
}

function trafficColumns(level: 1 | 2 | 3) {
  const cols = [{ key: 'one_level_channel', label: '一级渠道' }];
  if (level >= 2) cols.push({ key: 'two_level_channel', label: '二级渠道' });
  if (level >= 3) cols.push({ key: 'three_level_channel', label: '三级渠道' });
  return [
    ...cols,
    { key: 'uv', label: 'UV' },
    { key: 'pay_users', label: '支付用户数' },
    { key: 'uv_conversion_rate', label: 'UV转化率' },
    { key: 'net_sales', label: '净销售额' },
    { key: 'uv_value', label: '单UV价值' },
  ];
}

const TOTAL_ROW_LABELS = new Set(['总计', '合计', '小计']);

function isTrafficTotalRow(row: ChannelTrafficRow): boolean {
  return [row.one_level_channel, row.two_level_channel, row.three_level_channel]
    .some(value => !!value && TOTAL_ROW_LABELS.has(value.trim()));
}

function trafficSortValue(row: ChannelTrafficRow, key: string): SortValue {
  return row[key as keyof ChannelTrafficRow] as SortValue;
}

// 渠道维度列：按层级显示一级/二级/三级渠道
function LevelTable({ title, level, rows, loading, userLevel, extra }: LevelTableProps) {
  const channelCols = level; // 1, 2, or 3 dimension columns
  const totalCols = channelCols + 5;
  const totalRows = useMemo(() => rows.filter(isTrafficTotalRow), [rows]);
  const detailRows = useMemo(() => rows.filter(row => !isTrafficTotalRow(row)), [rows]);
  const { sort, toggleSort, sortedRows, setSort } = useTableSort<ChannelTrafficRow, string>(detailRows, trafficSortValue);
  const sortedTableRows = useMemo(() => [...totalRows, ...sortedRows], [totalRows, sortedRows]);

  useEffect(() => {
    if (sort && userLevel < 2 && isSiteSensitiveMetric(sort.key)) setSort(null);
  }, [sort, userLevel, setSort]);

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{title}</span>
        {extra}
      </div>
      <div className="table-wrap" style={{ position: 'relative', maxHeight: 420, overflow: 'auto' }}>
        <table style={{ minWidth: 720 }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
            <tr>
              <SortableHeader sortKey="one_level_channel" sort={sort} onSort={toggleSort} style={{ background: 'var(--surface2)', whiteSpace: 'nowrap' }}>一级渠道</SortableHeader>
              {level >= 2 && <SortableHeader sortKey="two_level_channel" sort={sort} onSort={toggleSort} style={{ background: 'var(--surface2)', whiteSpace: 'nowrap' }}>二级渠道</SortableHeader>}
              {level >= 3 && <SortableHeader sortKey="three_level_channel" sort={sort} onSort={toggleSort} style={{ background: 'var(--surface2)', whiteSpace: 'nowrap' }}>三级渠道</SortableHeader>}
              <SortableHeader sortKey="uv" sort={sort} onSort={toggleSort} align="right" style={{ background: 'var(--surface2)', whiteSpace: 'nowrap' }}>UV</SortableHeader>
              <SortableHeader sortKey="pay_users" sort={sort} onSort={toggleSort} align="right" style={{ background: 'var(--surface2)', whiteSpace: 'nowrap' }}>支付用户数</SortableHeader>
              <SortableHeader sortKey="uv_conversion_rate" sort={sort} onSort={toggleSort} align="right" style={{ background: 'var(--surface2)', whiteSpace: 'nowrap' }}>UV转化率</SortableHeader>
              <SortableHeader sortKey="net_sales" sort={sort} onSort={toggleSort} align="right" style={{ background: 'var(--surface2)', whiteSpace: 'nowrap' }} disabled={userLevel < 2 && isSiteSensitiveMetric('net_sales')}>净销售额</SortableHeader>
              <SortableHeader sortKey="uv_value" sort={sort} onSort={toggleSort} align="right" style={{ background: 'var(--surface2)', whiteSpace: 'nowrap' }} disabled={userLevel < 2 && isSiteSensitiveMetric('uv_value')}>单UV价值</SortableHeader>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr><td colSpan={totalCols}><TableLoadingCell /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={totalCols} style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>暂无数据</td></tr>
            ) : sortedTableRows.map((row, idx) => (
              <tr key={idx}>
                <td style={{ whiteSpace: 'nowrap' }}>{row.one_level_channel || '-'}</td>
                {level >= 2 && <td style={{ whiteSpace: 'nowrap' }}>{row.two_level_channel || '-'}</td>}
                {level >= 3 && <td style={{ whiteSpace: 'nowrap' }}>{row.three_level_channel || '-'}</td>}
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtInt(row.uv)}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtInt(row.pay_users)}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtPct(row.uv_conversion_rate)}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatSiteMetric('net_sales', row.net_sales, fmtNum, userLevel)}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatSiteMetric('uv_value', row.uv_value, fmtNum, userLevel)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && rows.length > 0 && <TableLoadingOverlay />}
      </div>
    </div>
  );
}

export default function SiteTrafficMonitoring() {
  const { shopName: site } = useSiteShopName();
  const adContext = useAppStore(s => s.adContext);
  const userLevel = adContext?.data_level ?? 1;
  const [date, setDate] = useState<string>(defaultDate());
  const [datePreset, setDatePreset] = useState<DatePreset>('yesterday');

  const [l1, setL1] = useState<ChannelTrafficRow[]>([]);
  const [l2, setL2] = useState<ChannelTrafficRow[]>([]);
  const [l3, setL3] = useState<ChannelTrafficRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const fetchData = useCallback(async () => {
    if (!site || !date) return;
    setLoading(true);
    setError('');
    try {
      const [r1, r2, r3] = await Promise.all([
        siteDataApi.getChannelTraffic({ site, date, level: 1 }),
        siteDataApi.getChannelTraffic({ site, date, level: 2 }),
        siteDataApi.getChannelTraffic({ site, date, level: 3 }),
      ]);
      setL1(r1.data?.list ?? []);
      setL2(r2.data?.list ?? []);
      setL3(r3.data?.list ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
      setL1([]); setL2([]); setL3([]);
    } finally {
      setLoading(false);
    }
  }, [site, date]);

  // 站点/日期就绪后自动查询
  useEffect(() => {
    if (site && date) fetchData();
  }, [site, date, fetchData]);

  useMarketingViewSection('page', {
    label: '页面筛选', state: { date, datePreset },
    restore: value => { setDate(value.date); setDatePreset(value.datePreset); },
    loading: loading, query: () => { fetchData(); },
    resolve: value => { return value.datePreset === 'custom' ? value : { ...value, datePreset: value.datePreset === 'today' ? 'custom' as const : value.datePreset, date: defaultDate(value.datePreset === 'today' ? 0 : 1) }; },
    summary: date,
    autoQuery: true,
  });

  const onDatePreset = (preset: DatePreset) => {
    setDatePreset(preset);
    if (preset !== 'custom') setDate(defaultDate(preset === 'today' ? 0 : 1));
  };

  return (
    <div className="page-section visible">
      {/* 筛选栏 */}
      <div className="data-filter-card">
        <div className="data-filter-row time">
          <span className="data-filter-inline-label">日期范围</span>
          <div className="page-tabs" style={{ marginBottom: 0 }}>
            {DATE_PRESETS.map(preset => (
              <div
                key={preset.key}
                className={`page-tab${datePreset === preset.key ? ' active' : ''}`}
                onClick={() => onDatePreset(preset.key)}
              >
                {preset.label}
              </div>
            ))}
          </div>
          {datePreset === 'custom' && (
            <div className="data-filter-date-range">
              <input type="date" className="form-input" value={date} onChange={e => setDate(e.target.value)} />
            </div>
          )}
        </div>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', marginBottom: 12, borderRadius: 8, background: 'rgba(220,38,38,.08)', color: '#dc2626', fontSize: 13 }}>
          {error}
        </div>
      )}

      <LevelTable
        title="一级渠道流量"
        level={1}
        rows={l1}
        loading={loading}
        userLevel={userLevel}
        extra={<ReportExportButton reportKey="site_traffic_channel" title="一级渠道流量" fileName={`一级渠道流量_${date}.csv`} params={{ site, date, level: 1 }} columns={trafficColumns(1)} disabled={!site || !date} />}
      />
      <LevelTable
        title="二级渠道流量"
        level={2}
        rows={l2}
        loading={loading}
        userLevel={userLevel}
        extra={<ReportExportButton reportKey="site_traffic_channel" title="二级渠道流量" fileName={`二级渠道流量_${date}.csv`} params={{ site, date, level: 2 }} columns={trafficColumns(2)} disabled={!site || !date} />}
      />
      <LevelTable
        title="三级渠道流量"
        level={3}
        rows={l3}
        loading={loading}
        userLevel={userLevel}
        extra={<ReportExportButton reportKey="site_traffic_channel" title="三级渠道流量" fileName={`三级渠道流量_${date}.csv`} params={{ site, date, level: 3 }} columns={trafficColumns(3)} disabled={!site || !date} />}
      />
    </div>
  );
}
